/**
 * Vehicle Field Display Configuration
 *
 * Defines the curated Edit Vehicle field inventory traced from the SRP publish
 * path. This is intentionally narrower than "all SRP fields"; computed,
 * internal, operational, and complex blob fields stay out of the dynamic form.
 */

export const EDIT_VEHICLE_SECTIONS = [
  { id: 'inventory_lifecycle', label: 'Inventory lifecycle' },
  { id: 'essential_information', label: 'Essential Information' },
  { id: 'vehicle_history', label: 'Vehicle History' },
  { id: 'pricing_mileage', label: 'Pricing & Mileage' },
  { id: 'colors_appearance', label: 'Colors & Appearance' },
  { id: 'engine_drivetrain', label: 'Engine & Drivetrain' },
  { id: 'description', label: 'Description' },
  { id: 'media_photos', label: 'Media / Photos' },
  { id: 'features_equipment', label: 'Features / Equipment' },
] as const

export type EditVehicleSectionId = (typeof EDIT_VEHICLE_SECTIONS)[number]['id']

export const EDIT_VEHICLE_SECTION_LABELS: Record<EditVehicleSectionId, string> =
  EDIT_VEHICLE_SECTIONS.reduce(
    (acc, section) => {
      acc[section.id] = section.label
      return acc
    },
    {} as Record<EditVehicleSectionId, string>,
  )

/** Explicit Edit Vehicle labels for specs/efficiency fields (avoid generic title-casing). */
export const EDIT_VEHICLE_SPECS_EFFICIENCY_FIELD_LABELS: Record<string, string> = {
  gvwr: 'GVWR',
  fuel_type: 'Fuel Type',
  is_model_e: 'Model E',
  passenger_capacity: 'Passenger Capacity',
  mpg_city_average: 'City MPG',
  mpg_highway_average: 'Highway MPG',
  mpg_city_low: 'City MPG Low',
  mpg_city_high: 'City MPG High',
  mpg_highway_low: 'Highway MPG Low',
  mpg_highway_high: 'Highway MPG High',
  ev_battery_range: 'EV Battery Range',
  ev_mpg_combined: 'EV Combined MPG',
}

export const EDIT_VEHICLE_SECTION_HELPERS: Partial<Record<EditVehicleSectionId, string>> = {
  essential_information: 'Core vehicle identity and merchandising details.',
  vehicle_history: 'Vehicle history and report indicators.',
  pricing_mileage: 'Pricing, mileage, and discount values shown to shoppers.',
  colors_appearance: 'Exterior and interior appearance shown to shoppers.',
  engine_drivetrain: 'Mechanical specifications and efficiency ratings.',
  description: 'Customer-facing descriptions and highlights.',
  media_photos: 'Upload and reorder shopper-facing vehicle photos.',
  features_equipment: 'Feature and equipment content.',
}

export const EDIT_VEHICLE_SECTION_FIELDS: Record<EditVehicleSectionId, readonly string[]> = {
  inventory_lifecycle: [
    'inventoryState',
    'inventoryUsageType',
    'inventoryStateMode',
    'inventoryUsageTypeMode',
  ],
  essential_information: [
    'condition',
    'certified_by_dealer',
    'conditions',
    'year',
    'make',
    'model',
    'trim',
    'stock_number',
    'body_type',
    'style_name',
  ],
  vehicle_history: ['has_carfax', 'carfax_one_owner', 'auto_check'],
  pricing_mileage: ['price', 'display_price', 'msrp', 'mileage', 'discount', 'date_in_stock_ts'],
  colors_appearance: [
    'exterior_color',
    'interior_color',
    'exterior_factory_color',
    'interior_factory_color',
  ],
  engine_drivetrain: [
    'drivetrain',
    'transmission',
    'transmission_description',
    'engine',
    'engine_cylinders',
    'engine_displacement',
    'fuel_type',
    'is_model_e',
    'doors',
    'passenger_capacity',
    'vehicle_type',
    'cab_style',
    'gvwr',
    'mpg_city_average',
    'mpg_highway_average',
    'mpg_city_low',
    'mpg_city_high',
    'mpg_highway_low',
    'mpg_highway_high',
    'ev_battery_range',
    'ev_mpg_combined',
  ],
  description: ['dealer_description', 'vehicle_highlights'],
  media_photos: ['dealer_photos'],
  features_equipment: [
    'safety_features',
    'mechanical_features',
    'interior_features',
    'exterior_features',
    'entertainment_features',
    'generic_equipment',
  ],
}

export const PRIORITY_FIELDS = {
  /**
   * Required fields - must be provided when adding a vehicle
   */
  required: ['year', 'make', 'model', 'condition'] as const,

  /**
   * Essential identification fields
   */
  essential: [
    'year',
    'make',
    'model',
    'trim',
    'stock_number',
    'body_type',
    'style_name',
    'state',
  ] as const,

  /**
   * Pricing and mileage information
   */
  pricing: ['price', 'display_price', 'msrp', 'mileage', 'discount'] as const,

  /**
   * Physical characteristics and appearance
   */
  physical: [
    'exterior_color',
    'interior_color',
    'exterior_factory_color',
    'interior_factory_color',
    'drivetrain',
    'transmission',
    'transmission_description',
    'engine',
    'engine_cylinders',
    'fuel_type',
    'is_model_e',
    'doors',
    'passenger_capacity',
  ] as const,

  /**
   * Performance and efficiency specifications
   */
  specifications: [
    'mpg_city_average',
    'mpg_highway_average',
    'mpg_city_low',
    'mpg_city_high',
    'mpg_highway_low',
    'mpg_highway_high',
    'ev_battery_range',
    'ev_mpg_combined',
    'vehicle_type',
    'cab_style',
    'engine_displacement',
    'gvwr',
  ] as const,

  /**
   * Marketing content and descriptions
   */
  content: ['dealer_description', 'vehicle_highlights'] as const,

  /**
   * Certifications and reports
   */
  certifications: [
    'certified_by_dealer',
    'certified_by_manufacturer',
    'has_carfax',
    'carfax_one_owner',
    'auto_check',
  ] as const,

  /**
   * Feature categories (optional - can be shown in expandable sections)
   */
  features: [
    'safety_features',
    'mechanical_features',
    'interior_features',
    'exterior_features',
    'entertainment_features',
    'generic_equipment',
  ] as const,
} as const

/**
 * Complete ordered list of fields to display in Add/Edit Vehicle form
 * Order determines visual presentation in the UI
 */
export const DISPLAY_FIELD_ORDER = [
  ...PRIORITY_FIELDS.essential,
  ...PRIORITY_FIELDS.pricing,
  ...PRIORITY_FIELDS.physical,
  ...PRIORITY_FIELDS.specifications,
  ...PRIORITY_FIELDS.content,
  ...PRIORITY_FIELDS.certifications,
  ...PRIORITY_FIELDS.features,
] as const

/**
 * Display fields to hide from the manual Add/Edit dynamic field list.
 *
 * `state` remains a valid backend Field Mapping target for feeds, but manual users should author
 * inventory state through the dedicated Inventory State operational control. The backend then
 * projects canonical inventoryState -> SRP.state.
 */
export const MANUAL_ADD_EDIT_EXCLUDED_DISPLAY_FIELDS = ['state'] as const

/**
 * Feed-backed vehicles manage shopper pricing in a dedicated pricing module, not
 * the general Edit Vehicle form / manual override flow.
 */
export const FEED_BACKED_EDIT_VEHICLE_EXCLUDED_DISPLAY_FIELDS = [
  'price',
  'display_price',
] as const

/**
 * Fields to always exclude from display (computed, system, or complex types)
 */
export const EXCLUDED_FIELDS = [
  // Complex structures requiring dedicated UI
  'features',
  'tech_specs',
  'tech_spec_photos',
  'stock_panoramas',
  'stock_videos',
  'safety_photos',
  'mechanical_photos',
  'interior_photos',
  'entertainment_photos',
  'exterior_photos',
  'srp_exterior_list_photos',
  'dealer_photos',
  'dealer_videos',
  'dealer_panoramas',
  'dealer_comments',
  'consumer_info',
  'incentives_json',
  'incentives_structured_full',
  'incentives_structured_stacks',
  'incentives_summary_json',
  'pricings',
  'pricings_structured',
  'expando_srp',
  'expando_vdp',
  'generic_text_1_literal',
  'generic_equipment_json',
  'options_json',
  'features_structured',
  'features_json',

  // Search and indexing fields
  'SearchTermList',
  'search_term_list',
  'vehicle_filter_ids',
  's0',
  'segments',

  // Computed/derived fields
  'make_model',
  'make_model_trim',
  'make_model_year',
  'make_model_year_sidebar',
  'display_price',
  'fuel_types',
  'isUsed',
  'has_price',
  'has_image',
  'has_incentives',
  'photo_count',
  'video_count',

  // Sidebar variants (computed for display)
  'cab_style_sidebar',
  'year_sidebar',
  'make_sidebar',
  'model_sidebar',
  'trim_sidebar',
  'body_type_sidebar',
  'drivetrain_sidebar',
  'transmission_sidebar',
  'exterior_color_sidebar',
  'interior_color_sidebar',
  'make_model_sidebar',
  'make_model_trim_sidebar',
  'conditions_sidebar',
  'fuel_type_sidebar',
  'vehicle_type_sidebar',
  'features_sidebar',
  'modifications_sidebar',
  'account_name_sidebar',
  'engine_liters_sidebar',
  'passenger_capacity_sidebar',
  'state_sidebar',
  'condition_sidebar',
  'exterior_factory_color_sidebar',
  'interior_factory_color_sidebar',
  'engine_cylinders_sidebar',
  'engine_aspiration_type_sidebar',
  'drivetrain_code_sidebar',
  'location_city_state_sidebar',

  // Lowercase/normalized variants
  'make_low',
  'model_low',
  'trim_low',

  // Text variants
  'doors_text',
  'passenger_capacity_text',
  'year_text',

  // System/metadata
  'id',
  'acode',
  'jazelId',
  'account_name',
  'LastModifiedDate',
  'modified_date',
  'date_in_stock',
  'chrome_style_id',
  'model_hash',
  'make_hash',
  'account_other_comments_permission',
  'image',
  'image_side',

  // A6 operational/workflow fields route through dedicated controls or APIs, not dynamic fields.
  'inventoryState',
  'inventoryStateMode',
  'inventoryStateChangedAt',
  'inventoryStateChangedBy',
  'inventoryUsageType',
  'inventoryUsageTypeMode',
  'inventoryUsageTypeChangedAt',
  'inventoryUsageTypeChangedBy',
  'websiteVisibility',
  'websiteVisibilityMode',
  'websiteVisibilityReason',
  'websiteVisibilityChangedAt',
  'websiteVisibilityChangedBy',
  'publishState',
  'archiveReason',
  'membershipStatus',
] as const

/**
 * Field type mapping for special input controls
 */
export const FIELD_TYPES = {
  boolean: [
    'used',
    'certified_by_dealer',
    'certified_by_manufacturer',
    'has_carfax',
    'carfax_one_owner',
    'auto_check',
    'is_model_e',
    'DLRCertified',
  ],
  number: [
    'year',
    'mileage',
    'price',
    'display_price',
    'msrp',
    'discount',
    'doors',
    'passenger_capacity',
    'engine_displacement',
    'gvwr',
    'mpg_city_average',
    'mpg_highway_average',
    'mpg_city_low',
    'mpg_city_high',
    'mpg_highway_low',
    'mpg_highway_high',
    'ev_battery_range',
    'ev_mpg_combined',
  ],
  textarea: [
    'dealer_description',
    'vehicle_highlights',
    'safety_features',
    'mechanical_features',
    'interior_features',
    'exterior_features',
    'entertainment_features',
    'generic_equipment',
  ],
  select: [
    'condition',
    'state',
    'drivetrain',
    'fuel_type',
    'vehicle_type',
    'body_type',
    'transmission',
  ],
  date: ['date_in_stock_ts'],
} as const

/**
 * Helper to get all displayable fields in order
 */
export function getDisplayFields(availableFields: string[]): string[] {
  const excludedSet = new Set<string>(EXCLUDED_FIELDS as readonly string[])

  // Filter to show only priority fields that are available and not excluded
  return (DISPLAY_FIELD_ORDER as readonly string[]).filter(
    (field) => availableFields.includes(field) && !excludedSet.has(field)
  )
}

export function getEditVehicleDynamicFieldsForSection(
  sectionId: EditVehicleSectionId,
  availableFields: string[],
): string[] {
  const excludedSet = new Set<string>(EXCLUDED_FIELDS as readonly string[])
  const manualExcludedSet = new Set<string>(MANUAL_ADD_EDIT_EXCLUDED_DISPLAY_FIELDS)
  const availableSet = new Set<string>(availableFields)

  return (EDIT_VEHICLE_SECTION_FIELDS[sectionId] as readonly string[]).filter(
    (field) =>
      availableSet.has(field) &&
      (!excludedSet.has(field) || field === 'display_price') &&
      !manualExcludedSet.has(field) &&
      field !== 'condition' &&
      field !== 'certified_by_dealer' &&
      field !== 'conditions' &&
      field !== 'vin' &&
      field !== 'dealer_photos',
  )
}

const CURATED_MANUAL_FILL_EXCLUDED_FIELDS = new Set<string>([
  ...EXCLUDED_FIELDS,
  ...MANUAL_ADD_EDIT_EXCLUDED_DISPLAY_FIELDS,
  'tags',
  'systemTags',
  'websiteVisibility',
  'website_visibility',
  'inventoryState',
  'inventoryUsageType',
  'inventoryStateMode',
  'inventoryUsageTypeMode',
  'dealer_photos',
  'vin',
])

const CURATED_MANUAL_FILL_FIELDS = new Set<string>(
  Object.entries(EDIT_VEHICLE_SECTION_FIELDS)
    .filter(([sectionId]) => sectionId !== 'inventory_lifecycle' && sectionId !== 'media_photos')
    .flatMap(([, fields]) => fields as readonly string[])
    .filter((field) => !CURATED_MANUAL_FILL_EXCLUDED_FIELDS.has(field)),
)

/**
 * Helper for manual Add/Edit dynamic display fields.
 *
 * This intentionally excludes SRP `state` from the normal display section to avoid showing two
 * editable concepts: SRP `state` and operational `inventoryState`. Feeds can still map into `state`;
 * manual users use the dedicated Inventory State control.
 */
export function getManualAddEditDisplayFields(availableFields: string[]): string[] {
  const manualExcludedSet = new Set<string>(MANUAL_ADD_EDIT_EXCLUDED_DISPLAY_FIELDS)
  return getDisplayFields(availableFields).filter((field) => !manualExcludedSet.has(field))
}

/**
 * Helper to check if a field should use a specific input type
 */
export function getFieldInputType(
  field: string,
): 'boolean' | 'number' | 'textarea' | 'select' | 'date' | 'text' {
  if (FIELD_TYPES.boolean.includes(field as any)) return 'boolean'
  if (FIELD_TYPES.number.includes(field as any)) return 'number'
  if (FIELD_TYPES.textarea.includes(field as any)) return 'textarea'
  if (FIELD_TYPES.select.includes(field as any)) return 'select'
  if (FIELD_TYPES.date.includes(field as any)) return 'date'
  return 'text'
}

export function isCuratedManualFillField(field: string): boolean {
  const normalized = String(field || '').trim()
  if (!normalized || !CURATED_MANUAL_FILL_FIELDS.has(normalized)) return false

  const inputType = getFieldInputType(normalized)
  return ['boolean', 'number', 'textarea', 'select', 'date', 'text'].includes(inputType)
}

